import { EventEmitter } from '@angular/core';
import * as L from 'leaflet';

export type SelectionMode = 'none' | 'rectangle' | 'polygon' | 'freehand';

export const SelectionControl = L.Control.extend({
	rectangleButton: undefined as any,
	polygonButton: undefined as any,
	finishPolygonButton: undefined as any,
	clearButton: undefined as any,
	printButton: undefined as any,
	deleteButton: undefined as any,
	selectionText: undefined as any,
	container: undefined as any,

	modeChanged: new EventEmitter<SelectionMode>(),
	clearClicked: new EventEmitter<void>(),
	printClicked: new EventEmitter<void>(),
	deleteClicked: new EventEmitter<void>(),
	finishPolygonClicked: new EventEmitter<void>(),

	_currentMode: 'none' as SelectionMode,
	_count: 0,
	_polygonPoints: 0,

	setCount(count: number) {
		this._count = count;
		this._updateDisplay();
	},

	setMode(mode: SelectionMode) {
		this._currentMode = mode;
		this._polygonPoints = 0;
		this._updateButtonStates();
	},

	setPolygonPoints(count: number) {
		this._polygonPoints = count;
		this._updateButtonStates();
	},

	_updateDisplay() {
		if (!this.selectionText) return;

		if (this._count > 0) {
			this.selectionText.innerHTML = `${this._count} ausgewählt`;
			this.selectionText.style.display = 'block';
			this.clearButton.style.display = 'block';
			this.printButton.style.display = 'block';
			this.deleteButton.style.display = 'block';
		} else {
			this.selectionText.style.display = 'none';
			this.clearButton.style.display = 'none';
			this.printButton.style.display = 'none';
			this.deleteButton.style.display = 'none';
		}
	},

	_updateButtonStates() {
		const activeColor = '#007bff';
		const inactiveColor = 'white';

		if (this.rectangleButton) {
			this.rectangleButton.style.backgroundColor =
				this._currentMode === 'rectangle' ? activeColor : inactiveColor;
			this.rectangleButton.style.color =
				this._currentMode === 'rectangle' ? 'white' : 'black';
		}
		if (this.polygonButton) {
			this.polygonButton.style.backgroundColor =
				this._currentMode === 'polygon' ? activeColor : inactiveColor;
			this.polygonButton.style.color =
				this._currentMode === 'polygon' ? 'white' : 'black';
		}
		if (this.finishPolygonButton) {
			const showFinish = this._currentMode === 'polygon' && this._polygonPoints >= 3;
			this.finishPolygonButton.style.display = showFinish ? 'flex' : 'none';
		}
	},

	onAdd: function(map: L.Map) {
		this.container = L.DomUtil.create('div', 'leaflet-control-selection');

		const bar = L.DomUtil.create('div', 'leaflet-bar', this.container);
		bar.style.display = 'flex';
		bar.style.flexDirection = 'column';

		this.rectangleButton = L.DomUtil.create('a', '', bar);
		this.rectangleButton.innerHTML = '<i class="bi bi-bounding-box"></i>';
		this.rectangleButton.href = '#';
		this.rectangleButton.title = 'Rechteck-Auswahl';
		this.rectangleButton.style.display = 'flex';
		this.rectangleButton.style.alignItems = 'center';
		this.rectangleButton.style.justifyContent = 'center';
		this.rectangleButton.style.width = '30px';
		this.rectangleButton.style.height = '30px';

		L.DomEvent.on(this.rectangleButton, 'click', (e: Event) => {
			L.DomEvent.preventDefault(e);
			L.DomEvent.stopPropagation(e);
			const newMode = this._currentMode === 'rectangle' ? 'none' : 'rectangle';
			this._currentMode = newMode;
			this._updateButtonStates();
			this.modeChanged.emit(newMode);
		});

		this.polygonButton = L.DomUtil.create('a', '', bar);
		this.polygonButton.innerHTML = '<i class="bi bi-pentagon"></i>';
		this.polygonButton.href = '#';
		this.polygonButton.title = 'Polygon-Auswahl';
		this.polygonButton.style.display = 'flex';
		this.polygonButton.style.alignItems = 'center';
		this.polygonButton.style.justifyContent = 'center';
		this.polygonButton.style.width = '30px';
		this.polygonButton.style.height = '30px';

		L.DomEvent.on(this.polygonButton, 'click', (e: Event) => {
			L.DomEvent.preventDefault(e);
			L.DomEvent.stopPropagation(e);
			const newMode = this._currentMode === 'polygon' ? 'none' : 'polygon';
			this._currentMode = newMode;
			this._polygonPoints = 0;
			this._updateButtonStates();
			this.modeChanged.emit(newMode);
		});

		this.finishPolygonButton = L.DomUtil.create('a', '', bar);
		this.finishPolygonButton.innerHTML = '<i class="bi bi-check-lg"></i>';
		this.finishPolygonButton.href = '#';
		this.finishPolygonButton.title = 'Polygon abschließen';
		this.finishPolygonButton.style.display = 'none';
		this.finishPolygonButton.style.alignItems = 'center';
		this.finishPolygonButton.style.justifyContent = 'center';
		this.finishPolygonButton.style.width = '30px';
		this.finishPolygonButton.style.height = '30px';
		this.finishPolygonButton.style.backgroundColor = '#28a745';
		this.finishPolygonButton.style.color = 'white';

		L.DomEvent.on(this.finishPolygonButton, 'click', (e: Event) => {
			L.DomEvent.preventDefault(e);
			L.DomEvent.stopPropagation(e);
			this.finishPolygonClicked.emit();
		});

		const separator = L.DomUtil.create('div', '', bar);
		separator.style.height = '1px';
		separator.style.backgroundColor = '#ccc';
		separator.style.margin = '2px 0';

		this.printButton = L.DomUtil.create('a', '', bar);
		this.printButton.innerHTML = '<i class="bi bi-printer"></i>';
		this.printButton.href = '#';
		this.printButton.title = 'Ausgewählte Punkte drucken';
		this.printButton.style.display = 'none';
		this.printButton.style.alignItems = 'center';
		this.printButton.style.justifyContent = 'center';
		this.printButton.style.width = '30px';
		this.printButton.style.height = '30px';

		L.DomEvent.on(this.printButton, 'click', (e: Event) => {
			L.DomEvent.preventDefault(e);
			L.DomEvent.stopPropagation(e);
			this.printClicked.emit();
		});

		this.deleteButton = L.DomUtil.create('a', '', bar);
		this.deleteButton.innerHTML = '<i class="bi bi-trash"></i>';
		this.deleteButton.href = '#';
		this.deleteButton.title = 'Ausgewählte Punkte löschen';
		this.deleteButton.style.display = 'none';
		this.deleteButton.style.alignItems = 'center';
		this.deleteButton.style.justifyContent = 'center';
		this.deleteButton.style.width = '30px';
		this.deleteButton.style.height = '30px';
		this.deleteButton.style.color = '#dc3545';

		L.DomEvent.on(this.deleteButton, 'click', (e: Event) => {
			L.DomEvent.preventDefault(e);
			L.DomEvent.stopPropagation(e);
			this.deleteClicked.emit();
		});

		this.clearButton = L.DomUtil.create('a', '', bar);
		this.clearButton.innerHTML = '<i class="bi bi-x-circle"></i>';
		this.clearButton.href = '#';
		this.clearButton.title = 'Auswahl aufheben';
		this.clearButton.style.display = 'none';
		this.clearButton.style.alignItems = 'center';
		this.clearButton.style.justifyContent = 'center';
		this.clearButton.style.width = '30px';
		this.clearButton.style.height = '30px';

		L.DomEvent.on(this.clearButton, 'click', (e: Event) => {
			L.DomEvent.preventDefault(e);
			L.DomEvent.stopPropagation(e);
			this.clearClicked.emit();
		});

		this.selectionText = L.DomUtil.create('div', '', this.container);
		this.selectionText.style.backgroundColor = 'white';
		this.selectionText.style.padding = '4px 8px';
		this.selectionText.style.borderRadius = '4px';
		this.selectionText.style.marginTop = '4px';
		this.selectionText.style.fontSize = '12px';
		this.selectionText.style.fontWeight = 'bold';
		this.selectionText.style.textAlign = 'center';
		this.selectionText.style.boxShadow = '0 1px 5px rgba(0,0,0,0.4)';
		this.selectionText.style.display = 'none';

		this._updateButtonStates();
		this._updateDisplay();

		return this.container;
	},

	onRemove: function(map: L.Map) {
		L.DomEvent.off(this.rectangleButton);
		L.DomEvent.off(this.polygonButton);
		L.DomEvent.off(this.finishPolygonButton);
		L.DomEvent.off(this.clearButton);
		L.DomEvent.off(this.printButton);
		L.DomEvent.off(this.deleteButton);
	}
});
